import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useState } from "react";
import { useServerFn } from "@/lib/server-fn-shim";
import { useQueryClient } from "@tanstack/react-query";
import { redeemInviteCode } from "@/lib/invites.functions";
import { Screen } from "@/components/Screen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { InviteCodeHelp } from "@/components/InviteCodeHelp";

export const Route = createFileRoute("/_authenticated/join")({
  validateSearch: (search: Record<string, unknown>) => {
    const out: { code?: string } = {};
    if (typeof search.code === "string") out.code = search.code;
    return out;
  },
  head: () => pageMeta({ title: 'Join a Trip — Roava by WGP Apps', description: 'Enter an invite code to join a shared trip on Roava.', path: '/join' }),
  component: Join,
});

function Join() {
  const redeem = useServerFn(redeemInviteCode);
  const nav = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const search = Route.useSearch();
  const [code, setCode] = useState(search.code ?? "");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await redeem({ data: { code } });
      console.log("[join] redeem result", result);
      const { tripId, role } = result;
      toast.success(t("join.success"));

      // Seed cache so /permissions has a tripId on first paint even if the
      // refetch is slow.
      qc.setQueryData(["my-trip"], (prev: any) => ({
        ...(prev ?? {}),
        trip: prev?.trip ?? { id: tripId, name: "", startDate: "", endDate: "" },
        isAdmin: role === "admin" ? true : Boolean(prev?.isAdmin),
        mySubmittedAt: prev?.mySubmittedAt ?? null,
      }));

      // Then force the real server response before we navigate.
      try {
        await qc.refetchQueries({ queryKey: ["my-trip"], exact: true });
      } catch (e) {
        console.error("[join] my-trip refetch failed", e);
      }

      const fresh = qc.getQueryData<any>(["my-trip"]);
      console.log("[join] post-refetch my-trip", fresh);

      nav({ to: "/permissions" });
    } catch (err: any) {
      console.error("[join]", err);
      const msg = err?.message ?? "";
      if (/invalid invite code/i.test(msg)) toast.error(t("join.err_invalid"));
      else if (/already been used/i.test(msg)) toast.error(t("join.err_used"));
      else if (/revoked/i.test(msg)) toast.error(t("join.err_revoked"));
      else if (/not authenticated|auth session/i.test(msg)) toast.error(t("join.err_auth"));
      else toast.error(t("join.err_generic"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen
      title={t("join.title")}
      subtitle={t("join.subtitle")}
      action={
        <Button
          variant="ghost"
          size="icon"
          onClick={() => nav({ to: "/trip" })}
          aria-label={t("join.back_aria")}
        >
          <ArrowLeft className="size-5" />
        </Button>
      }
    >
      <form onSubmit={submit} className="grid gap-4">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="MGL-XXXX-XXXX"
          className="h-16 text-center text-xl tracking-[0.2em] font-bold rounded-2xl font-mono uppercase"
          required
        />
        <InviteCodeHelp />
        <Button type="submit" disabled={busy || code.length < 6} className="h-14 rounded-2xl text-base font-semibold">
          {busy ? t("join.submitting") : t("join.submit")}
        </Button>
      </form>
    </Screen>
  );
}
